import React, { useState } from "react";
import { operatorsFor, valueKindFor } from "./broadcastEnums";

/**
 * ONE `{field, op, value}` rule of an audience filter, as three controls.
 *
 * ---------------------------------------------------------------------------
 * THE TWO RULES THIS ROW EXISTS TO ENFORCE
 * ---------------------------------------------------------------------------
 * 1. A FIELD WITH `available: false` IS RENDERED, DISABLED, AND SAYS WHY.
 *
 *    `location_id` is the live example. It is wired end to end — registry,
 *    validation, SQL — and the upstream table that would populate it has no
 *    rows. So a rule built on it is accepted by the server and matches nobody,
 *    and the author has no way to see the cause: the builder looks fine, the
 *    count says 0, and the natural reading of a zero is "my rules are wrong".
 *
 *    Offering it silently is therefore worse than not offering it. But HIDING
 *    it is worse still — the author who was told the feature shipped goes
 *    looking for the field, cannot find it, and concludes it was never built.
 *    So it stays in the list, disabled, carrying its reason in its own label.
 *    The disabled state is the honest one: "this exists, and it cannot help
 *    you yet".
 *
 * 2. CHANGING THE FIELD RESETS THE OPERATOR AND THE VALUE.
 *
 *    Operators are per TYPE. `within_last` is meaningless on an enum and `in`
 *    is meaningless on a relative time, so an operator carried over from the
 *    previous field is very likely invalid for the new one. Worse is the value:
 *    a `[lo, hi]` tuple left behind by a `between` and posted against a
 *    `within_last` is a 422 whose cause is invisible on screen — the row looks
 *    complete, because the control that would have shown the stale shape was
 *    replaced the moment the field changed. Resetting both is the only state
 *    from which the next two choices cannot contradict each other.
 *
 * Presentational: no fetching, no preview. The owner holds the rule list.
 */

/* ── Copy ─────────────────────────────────────────────────────────────── */

/**
 * What an unavailable field says when the registry offers no better reason.
 *
 * The registry's own `description` wins when it has one — the server knows
 * more about why a table is empty than this file ever can.
 */
const UNAVAILABLE_REASON = "No profile carries this yet";

const RANGE_HINT = "The upper bound is below the lower bound.";

/* ── Styling ──────────────────────────────────────────────────────────── */

const CONTROL =
  "rounded-lg border border-bc-border bg-white px-2.5 py-1.5 text-[13px] text-bc-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";

const NUMBER_CONTROL = `${CONTROL} w-24`;

const REMOVE_BUTTON =
  "rounded-lg px-2 py-1.5 text-[13px] text-bc-text-2 hover:bg-bc-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";

/* ── Field helpers ────────────────────────────────────────────────────── */

/**
 * The registry entry for a rule's field, or null.
 *
 * @param {Array<Object>} fields - Decoded field registry.
 * @param {string|null|undefined} key
 * @returns {Object|null}
 */
const fieldByKey = (fields, key) =>
  (Array.isArray(fields) ? fields : []).find((entry) => entry?.key === key) || null;

/**
 * Why a field cannot be used. Only meaningful when `available` is false.
 *
 * @param {Object} field
 * @returns {string}
 */
const reasonFor = (field) => field?.description || UNAVAILABLE_REASON;

/**
 * An enum option's wire id. Options pass through the decoder untouched, so
 * they arrive as bare scalars from some backends and `{id, label}` from others.
 *
 * @param {*} option
 * @returns {string}
 */
const optionId = (option) =>
  option && typeof option === "object"
    ? String(option.id ?? option.value ?? option.key ?? "")
    : String(option);

/**
 * An enum option's human label, falling back to its id.
 *
 * @param {*} option
 * @returns {string}
 */
const optionLabel = (option) =>
  option && typeof option === "object"
    ? String(option.label ?? optionId(option))
    : String(option);

/* ── Plain-language echo ──────────────────────────────────────────────── */

/**
 * "more than 21 days" is not what `more_than_ago` means. The operator label
 * carries the front half of the sentence and this carries the back half.
 */
const DAYS_SUFFIX = { more_than_ago: " ago" };

/**
 * A rule's value, as words.
 *
 * @param {string|null} kind - From `valueKindFor`.
 * @param {*} value
 * @param {string} op
 * @returns {string}
 */
const describeValue = (kind, value, op) => {
  if (kind === "days") {
    if (value === null || value === undefined || value === "") return "";
    return `${value} days${DAYS_SUFFIX[op] || ""}`;
  }
  if (kind === "range") {
    const [lo, hi] = Array.isArray(value) ? value : [];
    if (lo === undefined && hi === undefined) return "";
    return `${lo ?? "?"} and ${hi ?? "?"}`;
  }
  if (kind === "boolean") {
    if (value === null || value === undefined || value === "") return "";
    return value === true || value === "true" ? "Yes" : "No";
  }
  if (kind === "list") {
    const list = Array.isArray(value) ? value : [];
    return list.map((entry) => String(entry)).join(", ");
  }
  if (value === null || value === undefined) return "";
  return String(value);
};

/**
 * One rule as a sentence, e.g. "Subscription expiry in the next 21 days".
 *
 * Exported because the picker summarises a SAVED filter with the same words the
 * builder used to write it. Two independent phrasings of the same rule is how a
 * saved filter starts looking like a different filter once it is saved.
 *
 * @param {{field: string, op: string, value: *}} rule
 * @param {Array<Object>} fields - Decoded field registry.
 * @returns {string}
 */
export const describeRule = (rule, fields) => {
  const field = fieldByKey(fields, rule?.field);
  const label = field?.label || rule?.field || "";
  const operator = operatorsFor(field?.type).find((entry) => entry.id === rule?.op);
  const opLabel = operator?.label || rule?.op || "";
  const valueText = describeValue(operator?.value || null, rule?.value, rule?.op);
  return [label, opLabel, valueText].filter(Boolean).join(" ").trim();
};

/* ── Value editors ────────────────────────────────────────────────────── */

/**
 * A comma-separated multi-value input, for a list field with no enum options.
 *
 * Holds the RAW text while it is being typed and only derives from the array
 * when it is not. A strictly derived input cannot be typed into: the moment you
 * press "," the parse drops it and the caret jumps back a character.
 *
 * @param {Object} props
 * @param {string[]} props.value
 * @param {(next: string[]) => void} props.onChange
 * @param {string} props.label - Accessible name.
 * @returns {React.ReactElement}
 */
const CommaListInput = ({ value, onChange, label }) => {
  const [typed, setTyped] = useState(null);
  const list = Array.isArray(value) ? value : [];

  return (
    <input
      type="text"
      aria-label={label}
      placeholder="elite, pro"
      className={`${CONTROL} min-w-[10rem]`}
      value={typed === null ? list.join(", ") : typed}
      onChange={(event) => {
        const raw = event.target.value;
        setTyped(raw);
        onChange(
          raw
            .split(",")
            .map((entry) => entry.trim())
            .filter((entry) => entry !== "")
        );
      }}
      onBlur={() => setTyped(null)}
    />
  );
};

/**
 * Checkboxes for an enum field that shipped its options.
 *
 * @param {Object} props
 * @param {Array<*>} props.options
 * @param {*} props.value
 * @param {(next: string[]) => void} props.onChange
 * @returns {React.ReactElement}
 */
const OptionChecklist = ({ options, value, onChange }) => {
  const selected = (Array.isArray(value) ? value : []).map((entry) => String(entry));

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      {options.map((option) => {
        const id = optionId(option);
        const checked = selected.includes(id);
        return (
          <label key={id} className="inline-flex items-center gap-1.5 text-[13px] text-bc-text-2">
            <input
              type="checkbox"
              checked={checked}
              aria-label={optionLabel(option)}
              onChange={() =>
                onChange(
                  checked
                    ? selected.filter((entry) => entry !== id)
                    : [...selected, id]
                )
              }
            />
            <span>{optionLabel(option)}</span>
          </label>
        );
      })}
    </div>
  );
};

/**
 * The value control for one rule, chosen by `valueKindFor(type, op)`.
 *
 * @param {Object} props
 * @param {string|null} props.kind
 * @param {Object|null} props.field
 * @param {{field: string, op: string, value: *}} props.rule
 * @param {(value: *) => void} props.onValue
 * @param {number} props.index
 * @returns {React.ReactElement|null}
 */
const ValueEditor = ({ kind, field, rule, onValue, index }) => {
  const label = `Rule ${index + 1} value`;

  if (kind === "list") {
    const options = Array.isArray(field?.options) ? field.options : [];
    if (options.length > 0) {
      return (
        <OptionChecklist options={options} value={rule.value} onChange={onValue} />
      );
    }
    return <CommaListInput value={rule.value} onChange={onValue} label={label} />;
  }

  if (kind === "number") {
    return (
      <input
        type="number"
        aria-label={label}
        className={NUMBER_CONTROL}
        value={rule.value ?? ""}
        onChange={(event) =>
          onValue(event.target.value === "" ? null : Number(event.target.value))
        }
      />
    );
  }

  if (kind === "range") {
    const [lo, hi] = Array.isArray(rule.value) ? rule.value : [];
    const setBound = (position, raw) => {
      const next = Array.isArray(rule.value) ? [...rule.value] : [null, null];
      next[position] = raw === "" ? null : Number(raw);
      onValue(next);
    };
    // Mid-edit is the normal case: someone types the upper bound before the
    // lower one every time. So this HINTS and never blocks — no disabled Save,
    // no swallowed keystroke, no reordering the pair behind the author's back.
    const inverted =
      Number.isFinite(Number(lo)) &&
      Number.isFinite(Number(hi)) &&
      lo !== null &&
      hi !== null &&
      Number(hi) < Number(lo);

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <input
            type="number"
            aria-label={`Rule ${index + 1} lower bound`}
            className={NUMBER_CONTROL}
            value={lo ?? ""}
            onChange={(event) => setBound(0, event.target.value)}
          />
          <span className="text-[13px] text-bc-soft">and</span>
          <input
            type="number"
            aria-label={`Rule ${index + 1} upper bound`}
            className={NUMBER_CONTROL}
            value={hi ?? ""}
            onChange={(event) => setBound(1, event.target.value)}
          />
        </div>
        {inverted ? (
          <span data-testid="bc-rule-range-hint" className="text-xs text-bc-warn">
            {RANGE_HINT}
          </span>
        ) : null}
      </div>
    );
  }

  if (kind === "days") {
    const echo = describeValue("days", rule.value, rule.op);
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          aria-label={label}
          className={NUMBER_CONTROL}
          value={rule.value ?? ""}
          onChange={(event) =>
            onValue(event.target.value === "" ? null : Number(event.target.value))
          }
        />
        <span className="text-[13px] text-bc-text-2">days</span>
        {echo ? (
          <span data-testid="bc-rule-days-echo" className="text-xs text-bc-soft">
            {[
              operatorsFor(field?.type).find((entry) => entry.id === rule.op)?.label,
              echo,
            ]
              .filter(Boolean)
              .join(" ")}
          </span>
        ) : null}
      </div>
    );
  }

  if (kind === "boolean") {
    const name = `bc-rule-${index}-boolean`;
    return (
      <div role="radiogroup" aria-label={label} className="flex items-center gap-3">
        {[
          { id: "yes", label: "Yes", value: true },
          { id: "no", label: "No", value: false },
        ].map((choice) => (
          <label
            key={choice.id}
            className="inline-flex items-center gap-1.5 text-[13px] text-bc-text-2"
          >
            <input
              type="radio"
              name={name}
              aria-label={choice.label}
              checked={rule.value === choice.value}
              onChange={() => onValue(choice.value)}
            />
            <span>{choice.label}</span>
          </label>
        ))}
      </div>
    );
  }

  return null;
};

/* ── The row ──────────────────────────────────────────────────────────── */

/**
 * One rule of a filter: field, operator, value, remove.
 *
 * @param {Object} props
 * @param {{field: string, op: string, value: *}} props.rule - The rule being edited.
 * @param {Array<Object>} props.fields - Decoded field registry: `{key, label, type, available, options, description}`.
 * @param {(next: {field: string, op: string, value: *}) => void} props.onChange - Receives the WHOLE next rule.
 * @param {() => void} [props.onRemove] - Called by the remove button.
 * @param {number} [props.index=0] - Position in the list. Spoken in the remove button's name.
 * @returns {React.ReactElement}
 */
export const FilterRuleRow = ({ rule, fields, onChange, onRemove, index = 0 }) => {
  const current = rule || { field: "", op: "", value: null };
  const registry = Array.isArray(fields) ? fields : [];
  const field = fieldByKey(registry, current.field);
  const operators = operatorsFor(field?.type);
  const kind = valueKindFor(field?.type, current.op);

  /*
   * RULE 2 — the reset. See the header. The new rule keeps nothing but the
   * field: an empty `op` renders the operator select on its placeholder, and a
   * null `value` means no stale `[lo, hi]` can survive into a control that has
   * no idea what to do with it.
   */
  const handleField = (key) => onChange({ field: key, op: "", value: null });

  const handleOperator = (op) => onChange({ ...current, op, value: null });

  const handleValue = (value) => onChange({ ...current, value });

  return (
    <div
      data-testid={`bc-rule-${index}`}
      className="flex flex-wrap items-start gap-2 rounded-lg border border-bc-border bg-white p-2.5"
    >
      <select
        aria-label={`Rule ${index + 1} field`}
        className={CONTROL}
        value={current.field || ""}
        onChange={(event) => handleField(event.target.value)}
      >
        <option value="">Choose a field</option>
        {registry.map((entry) => (
          /*
           * RULE 1 — present, disabled, and carrying its own reason. Never
           * filtered out: a field that vanishes teaches nobody anything.
           */
          <option key={entry.key} value={entry.key} disabled={!entry.available}>
            {entry.available ? entry.label : `${entry.label} — ${reasonFor(entry)}`}
          </option>
        ))}
      </select>

      <select
        aria-label={`Rule ${index + 1} operator`}
        className={CONTROL}
        value={current.op || ""}
        disabled={operators.length === 0}
        onChange={(event) => handleOperator(event.target.value)}
      >
        <option value="">Choose an operator</option>
        {operators.map((operator) => (
          <option key={operator.id} value={operator.id}>
            {operator.label}
          </option>
        ))}
      </select>

      <ValueEditor
        kind={kind}
        field={field}
        rule={current}
        onValue={handleValue}
        index={index}
      />

      <button
        type="button"
        aria-label={`Remove rule ${index + 1}`}
        onClick={onRemove}
        className={`${REMOVE_BUTTON} ml-auto`}
      >
        Remove
      </button>

      {/* The reason again, in the body of the row, for the author who has
          already selected an unavailable field — a disabled <option> is not
          somewhere anyone looks twice. */}
      {field && !field.available ? (
        <p data-testid={`bc-rule-${index}-unavailable`} className="w-full text-xs text-bc-warn">
          {`${field.label} — ${reasonFor(field)}`}
        </p>
      ) : null}
    </div>
  );
};

export default FilterRuleRow;
